import moment from "moment";

export function sortTableHeadersByOrder(headers) {
  return [...headers].sort((a, b) => a.order - b.order);
}

/**
 * Sorts table data by a specified column key and updates the table state.
 *
 * This function supports three sorting modes:
 * - **Date sorting**: When `type` is "date", values are parsed as MM/DD/YYYY dates
 * - **Numeric sorting**: When both values are numeric strings, they're compared as numbers
 * - **String sorting**: Default fallback using locale-aware string comparison
 *
 * @param key - The primary key of the column to sort by
 * @param subKey - Optional nested key for sorting by a sub-property (e.g., `item[key][subKey]`)
 * @param tableDataToRender - The array of table row data to be sorted
 * @param sortAscending - If `true`, sorts in ascending order; if `false`, sorts in descending order
 * @param setTableDataToRender - React state setter function to update the sorted data
 * @param startIndex - The starting index for pagination (currently unused but reserved for future use)
 * @param endIndex - The ending index for pagination (currently unused but reserved for future use)
 * @param type - Optional sort type: "date" for date comparison, otherwise auto-detects numeric or string
 *
 * @example
 * ```typescript
 * // Sort by date column in descending order
 * sortTableData(
 *   "createdAt",
 *   undefined,
 *   tableData,
 *   false,
 *   setTableData,
 *   0,
 *   9,
 *   "date"
 * );
 *
 * // Sort by nested property (item.user.name)
 * sortTableData("user", "name", tableData, true, setTableData, 0, 9);
 * ```
 */
export const sortTableData = (
  key: string,
  subKey: string | undefined,
  tableDataToRender: any[],
  sortAscending: boolean,
  setTableDataToRender: React.Dispatch<React.SetStateAction<any[]>>,
  startIndex: number,
  endIndex: number,
  type?: string,
) => {
  const sortedArrayData = [...tableDataToRender].sort((a, b) => {
    const getValue = (item: any) => (subKey ? item[key][subKey] : item[key]);
    const valA = getValue(a);
    const valB = getValue(b);

    if (type === "date") {
      const dateA = parseMDYString(moment(valA).format("MM/DD/YYYY"));
      const dateB = parseMDYString(moment(valB).format("MM/DD/YYYY"));
      return sortAscending
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    }

    const isNumeric = (val: any) => /^-?\d+(\.\d+)?$/.test(val);

    if (isNumeric(valA) && isNumeric(valB)) {
      return sortAscending
        ? Number(valA) - Number(valB)
        : Number(valB) - Number(valA);
    }

    return sortAscending
      ? String(valA).localeCompare(String(valB))
      : String(valB).localeCompare(String(valA));
  });

  setTableDataToRender((prevData) => {
    const updatedTableData = [...sortedArrayData];
    return updatedTableData;
  });
};

function parseMDYString(dateStr: string): Date {
  const [month, day, year] = dateStr.split("/").map(Number);
  return new Date(year, month - 1, day);
}
